import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { DeleteFilled, EditFilled, QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import ImageRender from '../../utils/ImageRender';

// Actions
import { get_all_medals, set_medal_create_id, medal_delete } from '../../../redux/actions/medalsActions';
import { get_medal_types_desc } from '../../../utils/get_status_desc';

const MedalsTable = (props) => {
	const { messageApi, setIsModalOpen, setUpdate, setMedalToUpdate, pageSize, setPageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_medals_loading, all_medals, medal_delete_loading } = useSelector(state => state.medals);

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		dispatch(set_medal_create_id(record._id.$oid));
		setMedalToUpdate(record);
	}

	const deleteMedal = (record) => {
		let newSkip = skip;

		if ((all_medals.count - 1) % pageSize === 0) {
			newSkip -= pageSize;
			setSkip(newSkip);
			localStorage.setItem('medalsSkip', newSkip);
		}

		const filters = { skip: newSkip, limit: pageSize };
		dispatch(medal_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		{
			key: 'asset',
			title: t('form.label.image'),
			dataIndex: 'asset',
			render: (_, record) => (
				<ImageRender
					key={record.asset}
					url={`${process.env.REACT_APP_SERVER_URL}/api/game/medals/${record._id.$oid}/asset`}
					size={{ width: 50, height: 50 }}
				/>
			)
		},
		{
			key: 'name',
			title: t('form.label.name'),
			dataIndex: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('table.copyableText.copyId'), t('table.copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			key: 'description',
			title: t('form.label.description'),
			dataIndex: 'description'
		},
		{
			key: 'medal_type',
			title: t('form.label.medalType'),
			dataIndex: 'medal_type',
			render: (text) => (
				<Typography.Text>
					{text ? get_medal_types_desc(text) : '-'}
				</Typography.Text>
			)
		},
		{
			key: 'value',
			title: t('form.label.medalValue'),
			dataIndex: 'value'
		},
		{
			key: 'action',
			title: t('table.headers.actions'),
			dataIndex: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('buttons.edit')}>
						<Button
							type='primary'
							style={{ backgroundColor: '#80B4B9' }}
							shape='circle'
							size='large'
							icon={<EditFilled />}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('medals.delete.confirmTitle')}
						description={t('medals.delete.confirmText')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteMedal(record)}
						okButtonProps={{
							loading: medal_delete_loading
						}}
						okText={t('buttons.confirm')}
						cancelText={t('buttons.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<DeleteFilled />}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pagination.pageSize;
		setSkip(skip);
		localStorage.setItem('medalsSkip', skip);

		const limit = pagination.pageSize;
		setPageSize(limit);
		localStorage.setItem('medalsPageSize', limit);

		const medalsFilters = { skip, limit };
		dispatch(get_all_medals(medalsFilters));
	};

	return (
		<Spin spinning={all_medals_loading}>
			<Table
				columns={columns}
				dataSource={all_medals.medals}
				onChange={handleTableChange}
				scroll={{
					x: 'auto'
				}}
				pagination={{
					pageSize: pageSize, 
					total: all_medals.count,
					current: (skip / pageSize) + 1,
					showSizeChanger: true,
					showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('table.pagination.of')} ${total}`,
					showTitle: false,
					responsive: true,
					locale: { items_per_page: `/ ${t('table.pagination.page')}` }
				}}
			/>
		</Spin>
	)
}

MedalsTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setMedalToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	setPageSize: PropTypes.func.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default MedalsTable;
